import React, { useCallback, FormEvent, RefObject, useState } from 'react';
import { isDesktop } from '@westwing/ui-kit/breakPoints';
import ClearIcon from '@westwing/ui-kit/Icons/Svgs/ClearIcon';
import { CrossIcon } from '@westwing/ui-kit/Icons/StyledIcons';
import { SearchInputBoxLayout, ClearInputIcon, Input, SearchIcon, StyledForm } from './SearchInputBoxElements';
import { VictorQaClasses, BellaTestIds } from 'Common/qaIds';

const SEARCH_RESET_VALUE = '';

const proactivelyFetchSuggestionsModule = () => {
    // eslint-disable-next-line no-unused-expressions
    import('./Suggestions');
};

interface SearchInputProps {
    placeholder: string;
    value: string;
    onChange: (searchString: string) => void;
    onKeyUp: (keyCode: number) => void;
    onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    onFocus?: () => void;
    onClick: () => void;
    onClearClick: () => void;
    isSearchOverlayActive: boolean;
    inputRef?: RefObject<HTMLInputElement>;
}

const SearchInputBox: React.FC<SearchInputProps> = ({
    placeholder,
    value,
    onChange,
    onKeyUp,
    onKeyDown,
    onFocus,
    onClick,
    onClearClick,
    isSearchOverlayActive,
    inputRef,
}) => {
    const [isSearchFocused, setSearchFocused] = useState<boolean>(false);

    const handleInputValueChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            onChange(event.target.value);
            setSearchFocused(true);
            event.preventDefault();
        },
        [onChange]
    );

    const handleInputKeyUp = useCallback(
        (event: React.KeyboardEvent<HTMLInputElement>) => {
            onKeyUp(event.keyCode);
            event.preventDefault();
        },
        [onKeyUp]
    );

    const handleInputKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLInputElement>) => {
            if (onKeyDown) {
                onKeyDown(event);
            }
        },
        [onKeyDown]
    );

    const handleInputFocus = useCallback(() => {
        if (isDesktop() || isSearchOverlayActive) {
            setSearchFocused(true);
        }
        proactivelyFetchSuggestionsModule();
        onClick();
        if (onFocus) {
            onFocus();
        }
    }, [onFocus, onClick, isSearchOverlayActive]);

    const handleInputBlur = useCallback(() => {
        setSearchFocused(false);
    }, []);

    const handleClearClick = useCallback(() => {
        onChange(SEARCH_RESET_VALUE);
        onClearClick();
    }, [onClearClick, onChange]);

    // We need a form element to make ios display its native keyboard search button.
    const handleFormOnSubmit = (event: FormEvent<HTMLFormElement>) => event.preventDefault();

    return (
        <SearchInputBoxLayout
            data-testid="dynamic-search-input"
            role="search"
            isSearchOverlayActive={isSearchOverlayActive}
        >
            <StyledForm
                role="search"
                action=""
                onSubmit={handleFormOnSubmit}
                isSearchOverlayActive={isSearchOverlayActive}
            >
                <Input
                    type="search"
                    placeholder={placeholder}
                    value={value}
                    onChange={handleInputValueChange}
                    onKeyUp={handleInputKeyUp}
                    onKeyDown={handleInputKeyDown}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    ref={inputRef}
                    isSearchOverlayActive={isSearchOverlayActive}
                    isSearchFocused={isSearchFocused}
                    data-testid={BellaTestIds.searchInput}
                    className={VictorQaClasses.headerSearchInput}
                />
            </StyledForm>
            <div>
                {isSearchOverlayActive && value && value.length ? (
                    <ClearInputIcon
                        role="button"
                        onClick={handleClearClick}
                        className={VictorQaClasses.headerSearchInputClearIcon}
                        data-testid={BellaTestIds.searchInputClearIcon}
                    >
                        {isDesktop() ? <CrossIcon width={13} height={13} strokeWidth={5} /> : <ClearIcon />}
                    </ClearInputIcon>
                ) : (
                    <SearchIcon
                        onClick={handleInputFocus}
                        data-testid={BellaTestIds.searchInputIcon}
                        className={VictorQaClasses.headerSearchIcon}
                    />
                )}
            </div>
        </SearchInputBoxLayout>
    );
};

export default SearchInputBox;
